import axios from "axios";
import { useMutation } from "@tanstack/react-query";
import { financialDocumentContract } from "@common/contracts/financial-document.contract";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { getAgentChatUploadErrorMessage } from "./agentChatUploadErrors";

const client = getBackendAPIClient(financialDocumentContract);

interface UploadInput {
    workspaceId: string;
    file: File;
    onProgress?: (fraction: number) => void;
    signal?: AbortSignal;
}

interface UploadResult {
    financialDocumentId: number;
    fileName: string;
}

export function useUploadAgentChatAttachment() {
    return useMutation({
        mutationFn: async (input: UploadInput): Promise<UploadResult> => {
            let directUpload = false;

            try {
                const urlResp = await client.mcpRequestUploadUrl({
                    body: {
                        workspaceId: input.workspaceId,
                        fileName: input.file.name,
                    },
                });

                // PUT directly to S3 via raw axios — the presigned URL is the
                // credential, and backendClient's interceptor would inject an
                // Authorization header that some S3 providers reject.
                directUpload = true;
                await axios.put(urlResp.body.uploadUrl, input.file, {
                    headers: { "Content-Type": input.file.type },
                    signal: input.signal,
                    onUploadProgress: (event) => {
                        if (input.onProgress && event.total) {
                            input.onProgress(event.loaded / event.total);
                        }
                    },
                });
                directUpload = false;

                const confirmResp = await client.mcpConfirmUpload({
                    body: { uploadToken: urlResp.body.uploadToken },
                });

                return {
                    financialDocumentId: confirmResp.body.documentId,
                    fileName: confirmResp.body.fileName,
                };
            } catch (error) {
                throw new Error(
                    getAgentChatUploadErrorMessage(error, { directUpload }),
                );
            }
        },
    });
}
